import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Col, DatePicker, Form, Input, InputNumber, message, Modal,
  Row, Select, Space, Table, Tabs, Tag, Typography
} from 'antd';
import {
  PlusOutlined, MinusCircleOutlined, SaveOutlined,
  CheckOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { voucherApi } from './voucherApi';
import { STATUS_COLOR, STATUS_LABEL } from './voucherConstants';
// Stage 6B.7D — read-only audit log + accounting attachments panels.
import AuditLogPanel from '../AuditLogPanel';
import AttachmentsPanel from '../AttachmentsPanel';
// Stage 6B.9F — period guard + DR/CR + balance validators (pure JS).
import { accountsApi } from '../accountsApi';
import {
  classifyPeriodForDate, periodGuardError, loadPeriodsForGuard
} from '../accountingPeriodGuard';
import { validateVoucherLines } from '../voucherLinesValidator';
import '../accounts.css';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Journal Voucher Add / Edit / View modal (Stage 6B.4).
//
// Centered modal (1080-wide), accounting-friendly:
//   - Header (Voucher No read-only, Voucher Date, Narration, Status pill)
//   - Lines grid: Ledger (searchable dropdown) / Debit / Credit / Narration
//   - Footer totals: Total Debit / Total Credit / Difference
//   - Add Line / Remove Line buttons
//   - View / posted / cancelled modes are READ-ONLY
//
// Posting + cancellation are NOT in this component — the parent page
// owns the Approval-MFA flow because Approval MFA stacks an external
// modal that must NOT overlap with this one.
// ─────────────────────────────────────────────────────────────────

const STATUS_VIEW_MODES = new Set(['view']);
const READONLY_STATUSES  = new Set(['posted', 'cancelled']);

function emptyLine(lineNo) {
  return {
    key:             `new-${lineNo}-${Math.random().toString(36).slice(2, 8)}`,
    lineNo,
    ledgerAccountId: null,
    debitAmount:     0,
    creditAmount:    0,
    narration:       ''
  };
}

function defaultsFor(mode, record) {
  if (mode === 'add' || !record) {
    return {
      voucherDate: dayjs(),
      narration:   '',
      lines: [emptyLine(1), emptyLine(2)]
    };
  }
  return {
    voucherDate: record.voucherDate ? dayjs(record.voucherDate) : dayjs(),
    narration:   record.narration || '',
    lines: (record.lines || []).map((l, i) => ({
      key:             l.id || `existing-${l.lineNo}-${i}`,
      lineNo:          l.lineNo || (i + 1),
      ledgerAccountId: l.ledgerAccountId,
      debitAmount:     Number(l.debitAmount)  || 0,
      creditAmount:    Number(l.creditAmount) || 0,
      narration:       l.narration || ''
    }))
  };
}

// Inline ledger dropdown — searchable, server-mode-friendly.
function LedgerSelect({ value, onChange, disabled, lookupCache }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  // Seed the option list with the currently-selected ledger (if any)
  // even before the user types — otherwise the value displays as a bare GUID.
  useEffect(() => {
    if (value && lookupCache && lookupCache.has(value)) {
      const row = lookupCache.get(value);
      setOptions((prev) => {
        if (prev.find((p) => p.id === value)) return prev;
        return [{ ...row }, ...prev];
      });
    }
  }, [value, lookupCache]);

  // First-open fetch (no query) so the user sees a sensible default list.
  const onOpen = useCallback(async (open) => {
    if (!open || options.length > 0) return;
    setLoading(true);
    try {
      const rows = await voucherApi.ledgerLookup('', 100);
      if (lookupCache) for (const r of rows) lookupCache.set(r.id, r);
      setOptions(rows);
    } catch (_e) { /* swallow — UI will show empty */ }
    finally { setLoading(false); }
  }, [options.length, lookupCache]);

  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await voucherApi.ledgerLookup(text || '', 100);
        if (lookupCache) for (const r of rows) lookupCache.set(r.id, r);
        setOptions(rows);
      } catch (_e) { /* noop */ }
      finally { setLoading(false); }
    }, 200);
  }, [lookupCache]);

  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      showSearch
      placeholder="Search ledger…"
      value={value || undefined}
      onChange={(v) => onChange(v)}
      onDropdownVisibleChange={onOpen}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      notFoundContent={loading ? 'Loading…' : 'No ledgers'}
      options={options.map((o) => ({
        value: o.id,
        label: (
          <span>
            <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{o.accountCode}</code>
            {o.accountName}
            <span style={{ marginLeft: 6, fontSize: 11, color: '#999' }}>
              · {o.accountType}
            </span>
          </span>
        )
      }))}
    />
  );
}

function fmtRupee(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function JournalVoucherModal({
  open, mode, record, onClose, onSaved
}) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState([emptyLine(1), emptyLine(2)]);
  const [periods, setPeriods] = useState([]);
  const [voucherDate, setVoucherDate] = useState(null);
  const lookupCacheRef = useRef(new Map());

  const isView    = STATUS_VIEW_MODES.has(mode);
  const isPosted  = record && READONLY_STATUSES.has(record.status);
  const readOnly  = isView || isPosted;
  const status    = record ? record.status : 'draft';

  // Stage 6B.9F — Period guard. Fetch monthly periods once when the
  // modal opens so the post-button gate can warn the operator about
  // locked / closed / missing periods BEFORE the round-trip.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadPeriodsForGuard(accountsApi).then((out) => {
      if (cancelled) return;
      setPeriods((out && out.rows) || []);
    });
    return () => { cancelled = true; };
  }, [open]);

  const periodClassification = useMemo(
    () => classifyPeriodForDate(periods, voucherDate),
    [periods, voucherDate]
  );
  const periodGuard = useMemo(
    () => periodGuardError(periodClassification),
    [periodClassification]
  );

  // Seed lookup cache with the loaded record's lines so the select
  // displays the readable label immediately on edit/view open.
  useEffect(() => {
    if (!record || !Array.isArray(record.lines)) return;
    // Fire a lookup with no filter to populate the option list — the
    // LedgerSelect will pull from cache for the row's selected ledger.
    // We don't await; the cache fills as soon as the first dropdown opens.
    voucherApi.ledgerLookup('', 100).then((rows) => {
      for (const r of rows) lookupCacheRef.current.set(r.id, r);
    }).catch(() => {});
  }, [record]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    const init = defaultsFor(mode, record);
    form.setFieldsValue({
      voucherDate: init.voucherDate,
      narration:   init.narration
    });
    setLines(init.lines);
    setVoucherDate(init.voucherDate);
  }, [open, mode, record, form]);

  const totals = useMemo(() => {
    let d = 0, c = 0;
    for (const ln of lines) {
      d += Number(ln.debitAmount)  || 0;
      c += Number(ln.creditAmount) || 0;
    }
    d = Math.round(d * 100) / 100;
    c = Math.round(c * 100) / 100;
    return { totalDebit: d, totalCredit: c, difference: Math.round((d - c) * 100) / 100, balanced: d === c && d > 0 };
  }, [lines]);

  const patchLine = useCallback((key, patch) => {
    setLines((prev) => prev.map((l) => l.key === key ? { ...l, ...patch } : l));
  }, []);

  const addLine = useCallback(() => {
    setLines((prev) => [...prev, emptyLine(prev.length + 1)]);
  }, []);

  const removeLine = useCallback((key) => {
    setLines((prev) => {
      const next = prev.filter((l) => l.key !== key)
        .map((l, i) => ({ ...l, lineNo: i + 1 }));
      // Always keep at least 2 rows visible (even if empty) so the
      // operator can quickly continue.
      while (next.length < 2) next.push(emptyLine(next.length + 1));
      return next;
    });
  }, []);

  const validateLinesShape = useCallback(() => {
    const errs = [];
    if (lines.length < 2) errs.push('Voucher must have at least two lines.');
    for (let i = 0; i < lines.length; i += 1) {
      const ln = lines[i];
      if (!ln.ledgerAccountId) errs.push(`Line ${ln.lineNo}: ledger is required.`);
      const dr = Number(ln.debitAmount)  || 0;
      const cr = Number(ln.creditAmount) || 0;
      if (dr > 0 && cr > 0) errs.push(`Line ${ln.lineNo}: cannot have both debit and credit.`);
      if (dr === 0 && cr === 0) errs.push(`Line ${ln.lineNo}: missing amount.`);
      if (dr < 0 || cr < 0)   errs.push(`Line ${ln.lineNo}: amounts must be non-negative.`);
    }
    return errs;
  }, [lines]);

  const buildPayload = useCallback(async () => {
    const headerValues = await form.validateFields();
    return {
      voucherDate: headerValues.voucherDate.format('YYYY-MM-DD'),
      narration:   headerValues.narration || null,
      lines: lines.map((l) => ({
        lineNo:          l.lineNo,
        ledgerAccountId: l.ledgerAccountId,
        debitAmount:     Number(l.debitAmount)  || 0,
        creditAmount:    Number(l.creditAmount) || 0,
        narration:       l.narration || null
      }))
    };
  }, [form, lines]);

  const saveDraft = useCallback(async () => {
    // Be lenient at draft save — only require valid SHAPE (each line
    // is DR xor CR, ledger present). Balance is not required.
    const shapeErrs = validateLinesShape();
    if (shapeErrs.length) {
      Modal.warning({
        title: 'Please fix these issues',
        content: <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13 }}>{shapeErrs.join('\n')}</pre>,
        okText: 'Close'
      });
      return;
    }
    setBusy(true);
    try {
      const payload = await buildPayload();
      let saved;
      if (mode === 'add') {
        saved = await voucherApi.createDraft(payload);
        message.success(`Draft ${saved.voucherNo} saved.`);
      } else if (mode === 'edit') {
        saved = await voucherApi.updateDraft(record.id, payload);
        message.success('Draft updated.');
      }
      onSaved && onSaved(saved);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Save failed.');
    } finally { setBusy(false); }
  }, [buildPayload, validateLinesShape, mode, record, onSaved]);

  const title = useMemo(() => {
    if (mode === 'add')  return 'New Journal Voucher';
    const vn = record && record.voucherNo ? ' — ' + record.voucherNo : '';
    if (mode === 'view') return `View Journal Voucher${vn}`;
    return `Edit Journal Voucher${vn}`;
  }, [mode, record]);

  const lineColumns = [
    { title: '#', key: 'lineNo', width: 50, align: 'center',
      render: (_v, row) => row.lineNo },
    { title: 'Ledger', key: 'ledger', width: 380,
      render: (_v, row) => (
        <LedgerSelect
          value={row.ledgerAccountId}
          disabled={readOnly}
          lookupCache={lookupCacheRef.current}
          onChange={(v) => patchLine(row.key, { ledgerAccountId: v })}
        />
      ) },
    { title: 'Debit (₹)', key: 'debit', width: 160, align: 'right',
      render: (_v, row) => (
        <InputNumber
          min={0} step={1} disabled={readOnly}
          style={{ width: '100%' }}
          value={row.debitAmount || null}
          formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
          parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0}
          onChange={(n) => {
            const v = Number(n) || 0;
            patchLine(row.key, {
              debitAmount: v,
              creditAmount: v > 0 ? 0 : row.creditAmount
            });
          }}
        />
      ) },
    { title: 'Credit (₹)', key: 'credit', width: 160, align: 'right',
      render: (_v, row) => (
        <InputNumber
          min={0} step={1} disabled={readOnly}
          style={{ width: '100%' }}
          value={row.creditAmount || null}
          formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
          parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0}
          onChange={(n) => {
            const v = Number(n) || 0;
            patchLine(row.key, {
              creditAmount: v,
              debitAmount: v > 0 ? 0 : row.debitAmount
            });
          }}
        />
      ) },
    { title: 'Narration', key: 'narration',
      render: (_v, row) => (
        <Input
          disabled={readOnly} maxLength={500} value={row.narration || ''}
          onChange={(e) => patchLine(row.key, { narration: e.target.value })}
        />
      ) },
    ...(readOnly ? [] : [{
      title: '', key: '__del__', width: 48, align: 'center',
      render: (_v, row) => (
        <Button type="text" danger icon={<MinusCircleOutlined />}
          disabled={lines.length <= 2}
          onClick={() => removeLine(row.key)} />
      )
    }])
  ];

  const footer = (
    <Space>
      {!readOnly ? (
        <Button type="primary" icon={<SaveOutlined />} loading={busy} onClick={saveDraft}>
          {mode === 'add' ? 'Save Draft' : 'Save Changes'}
        </Button>
      ) : null}
      <Button onClick={onClose}>Close</Button>
    </Space>
  );

  // Stage UI-FIX (Journal Voucher focus loss): the previous structure
  // declared `function DetailsBody()` inside this parent component's
  // body, so every parent re-render (state change on lines, voucherDate,
  // periodGuard, etc.) created a brand new function identity. React
  // treated `<DetailsBody />` as a fresh component type each render and
  // unmounted + remounted the entire form sub-tree → focus on Narration /
  // amount cells was lost after a single keystroke. Replacing the inner
  // component with a JSX value (`detailsBody`) keeps the underlying
  // <Form>, <Input>, <Select>, <Table> element TYPES stable across
  // renders so React patches them in-place. Validation rules, lines
  // state, totals, period guard and save flow are unchanged.
  const detailsBody = (<>

      <Form className="erp-accounts-form" form={form} layout="vertical" preserve>
        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item label="Voucher No">
              <Input value={record?.voucherNo || (mode === 'add' ? '(auto-generated)' : '')} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Voucher Date" name="voucherDate"
              rules={[{ required: true, message: 'Voucher date is required' }]}>
              <DatePicker style={{ width: '100%' }} disabled={readOnly}
                onChange={(d) => setVoucherDate(d)} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Narration" name="narration">
              <Input maxLength={2000} disabled={readOnly}
                placeholder="Short description of the journal entry" />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {!readOnly ? (
        <Space style={{ marginBottom: 6 }}>
          <Button size="small" icon={<PlusOutlined />} onClick={addLine}>Add Line</Button>
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            Use Debit OR Credit per line — never both. Posting requires Debit = Credit and total &gt; 0.
          </Text>
        </Space>
      ) : null}
      <div className="erp-accounts-lines">
      <Table
        size="small"
        rowKey="key"
        columns={lineColumns}
        dataSource={lines}
        pagination={false}
        scroll={{ x: 980 }}
      />
      </div>
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
        <Space size={24}>
          <span><Text type="secondary">Total Debit:</Text>
            <Text strong style={{ marginLeft: 6 }}>₹ {fmtRupee(totals.totalDebit)}</Text></span>
          <span><Text type="secondary">Total Credit:</Text>
            <Text strong style={{ marginLeft: 6 }}>₹ {fmtRupee(totals.totalCredit)}</Text></span>
          <span>
            <Text type="secondary">Difference:</Text>
            <Text strong style={{ marginLeft: 6,
              color: totals.balanced ? '#3f8600' : '#cf1322' }}>
              ₹ {fmtRupee(Math.abs(totals.difference))} {totals.balanced
                ? <CheckOutlined />
                : <CloseCircleOutlined />}
            </Text>
          </span>
        </Space>
      </div>

      {!readOnly && !totals.balanced ? (
        <Alert type="info" showIcon style={{ marginTop: 12 }}
          message="Voucher is not yet balanced — draft can be saved, but posting will be blocked until Debit = Credit and total > 0." />
      ) : null}
      {/* Stage 6B.9F — Period guard banner. Informational on draft
          (we still let the operator save a draft for a locked period
          if that's what they need); the page-level Post handler is
          the authoritative gate that blocks the round-trip. */}
      {!readOnly && periodGuard ? (
        <Alert
          type={periodGuard.code === 'VOUCHER_PERIOD_CLOSED' ? 'error' : 'warning'}
          showIcon style={{ marginTop: 12 }}
          message="Accounting period check"
          description={periodGuard.message} />
      ) : null}
      {status === 'cancelled' && record && record.cancelReason ? (
        <Alert type="error" showIcon style={{ marginTop: 12 }}
          message="Cancelled voucher"
          description={<><b>Reason:</b> {record.cancelReason}</>} />
      ) : null}
    </>);

  return (
    <Modal
      title={
        <span>
          {title}
          {record && record.status ? (
            <Tag color={STATUS_COLOR[record.status] || 'default'}
                 style={{ marginInlineStart: 12 }}>
              {STATUS_LABEL[record.status] || record.status}
            </Tag>
          ) : null}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      width={1080}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-accounts-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', padding: '12px 16px' } }}
    >
      {/* Stage 6B.7D — Audit + Attachments tabs are only rendered in
          view mode (when a persisted voucher exists). Add / Edit
          surfaces stay tab-less so the operator's workflow is
          identical to Stages 6B.4 → 6B.7. */}
      {mode === 'view' && record && record.id ? (
        <Tabs size="small" defaultActiveKey="details" items={[
          { key: 'details', label: 'Details', children: detailsBody },
          { key: 'audit', label: 'Audit', children: (
            <AuditLogPanel entityType="accounting_voucher" entityId={record.id} />
          ) },
          { key: 'attachments', label: 'Attachments', children: (
            <AttachmentsPanel
              entityType="accounting_voucher" entityId={record.id}
              canUpload canDelete
            />
          ) }
        ]} />
      ) : (
        detailsBody
      )}
    </Modal>
  );
}
